import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import { environment } from 'environments/environment';
import { SessionService } from 'app/core/auth/session.service';
import { COOKIE_SESSION_REQUEST } from 'app/core/auth/session.types';

/** Cada cuánto se reporta actividad, y cuánto sin tocar mouse/teclado deja de contar como uso. */
const PULSO_MS = 60_000;
const INACTIVO_MS = 5 * 60_000;

/**
 * Registro de pantallas usadas: cada navegación (NavigationEnd) de un usuario
 * autenticado se anota en SNV vía POST /api/uso/nav. Usuario, empresa y sesión
 * salen de la cookie en el backend; acá solo se manda la ruta (sin query).
 * Lo lee el dashboard del superadmin (adm/dsh → "Pantallas").
 *
 * Además, cada minuto con la pestaña visible y alguna interacción en los últimos
 * 5 min se manda POST /api/uso/actividad (tabla SAC): son los tramos de uso real
 * de la línea de tiempo de adm/dsh — el latido de presencia corre aunque nadie
 * esté frente a la pantalla, así que no sirve para medir uso.
 */
@Injectable({ providedIn: 'root' })
export class NavLogService {
    private started = false;
    private readonly url = `${environment.urlApi.replace(/\/$/, '')}/api/uso/nav`;
    private readonly urlActividad = `${environment.urlApi.replace(/\/$/, '')}/api/uso/actividad`;
    private ultimaAccion = Date.now();

    constructor(private http: HttpClient, private router: Router, private session: SessionService, private zone: NgZone) { }

    start(): void {
        if (this.started) return;
        this.started = true;
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(e => e.urlAfterRedirects.split(/[?#]/)[0]),
            filter(ruta => !ruta.startsWith('/aut/') && this.session.snapshot.status === 'authenticated'),
            // Una misma ruta repetida (p.ej. cambiar solo los query params) no es otra visita.
            distinctUntilChanged(),
            switchMap(ruta => this.http.post<void>(this.url, { ruta }, {
                withCredentials: true,
                context: new HttpContext().set(COOKIE_SESSION_REQUEST, true),
            }).pipe(catchError(() => of(undefined)))),
        ).subscribe();
        this.iniciarActividad();
    }

    /** Fuera de la zona de Angular: mousemove no debe disparar detección de cambios. */
    private iniciarActividad(): void {
        this.zone.runOutsideAngular(() => {
            const marcar = () => { this.ultimaAccion = Date.now(); };
            for (const ev of ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll']) {
                window.addEventListener(ev, marcar, { passive: true, capture: true });
            }
            setInterval(() => {
                if (document.visibilityState !== 'visible') return;
                if (Date.now() - this.ultimaAccion > INACTIVO_MS) return;
                if (this.session.snapshot.status !== 'authenticated') return;
                if (this.router.url.startsWith('/aut/')) return;
                this.http.post<void>(this.urlActividad, {}, {
                    withCredentials: true,
                    context: new HttpContext().set(COOKIE_SESSION_REQUEST, true),
                }).pipe(catchError(() => of(undefined))).subscribe();
            }, PULSO_MS);
        });
    }
}
